import { OutOfPocketMaxState, QmeState } from "#/lib/carrot-api/types/Subsidy"
import { Percentages } from "../hooks/useReimbursementRulePercentages"
import { PiiText } from "#/services/tracking"
import { Box, Stack, Typography } from "@carrotfertility/carotene-core"
import React, { FC } from "react"
import { FormattedMessage } from "react-intl"

type YourCostShareOopMaxRulesProps = {
  companyDisplayName: string
  isDefaultCategory: boolean
  getPercentages: (qme: QmeState, oop: OutOfPocketMaxState) => Percentages
}

export const YourCostShareOopMaxRules: FC<YourCostShareOopMaxRulesProps> = ({
  companyDisplayName,
  isDefaultCategory,
  getPercentages
}) => {
  const oopMet = getPercentages(QmeState.ANY, OutOfPocketMaxState.OOP_MAX_MET)
  const oopNotMet = getPercentages(QmeState.ANY, OutOfPocketMaxState.OOP_MAX_NOT_MET)
  const rowColumns = "minmax(0, 1fr) 72px 72px"

  return (
    <Box
      sx={(theme) => ({
        padding: theme.spacing(theme.tokens.spacing.md),
        display: "flex",
        backgroundColor: theme.palette.background.default,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius
      })}
    >
      <Box width="100%">
        <Box
          sx={(theme) => ({
            paddingBlockStart: theme.spacing(theme.tokens.spacing.xs),
            paddingBlockEnd: theme.spacing(theme.tokens.spacing.sm),
            marginBlockEnd: theme.spacing(theme.tokens.spacing.xs),
            display: "grid",
            gridTemplateColumns: rowColumns,
            columnGap: theme.spacing(theme.tokens.spacing.lg),
            alignItems: "start",
            inlineSize: "100%",
            borderBlockEnd: `1px solid ${theme.palette.divider}`
          })}
        >
          <Typography variant="body1" fontWeight="bold" color={(theme) => theme.palette.text.secondary}>
            {isDefaultCategory ? (
              <FormattedMessage
                defaultMessage="Approved expenses"
                description="Table column header for expense types in the cost share table"
              />
            ) : (
              <FormattedMessage
                defaultMessage="Approved expenses in this category"
                description="Table column header for expense types when showing a specific benefit category"
              />
            )}
          </Typography>
          <Typography
            variant="body1"
            fontWeight="bold"
            color={(theme) => theme.palette.text.secondary}
            sx={{ whiteSpace: "nowrap", textAlign: "end" }}
          >
            <FormattedMessage
              defaultMessage="You pay"
              description="Table column header for the member's cost share percentage"
            />
          </Typography>
          <Typography
            variant="body1"
            fontWeight="bold"
            color={(theme) => theme.palette.text.secondary}
            textAlign="right"
          >
            <FormattedMessage
              defaultMessage="{companyDisplayName} pays"
              description="Table column header for the employer's cost share percentage"
              values={{ companyDisplayName: <PiiText>{companyDisplayName}</PiiText> }}
            />
          </Typography>
        </Box>

        <Box
          sx={(theme) => ({
            paddingBlockStart: theme.spacing(theme.tokens.spacing.xs),
            paddingBlockEnd: theme.spacing(theme.tokens.spacing.xs),
            display: "grid",
            gridTemplateColumns: rowColumns,
            columnGap: theme.spacing(theme.tokens.spacing.lg),
            alignItems: "start",
            inlineSize: "100%"
          })}
        >
          <Typography variant="body1" fontWeight="bold" color={(theme) => theme.palette.text.secondary}>
            <FormattedMessage
              defaultMessage="Before you meet your deductible"
              description="Row label in cost share table for the insurance tier before deductible is met"
            />
          </Typography>
          <Typography variant="body1" color={(theme) => theme.palette.text.secondary} textAlign="right">
            <FormattedMessage
              defaultMessage="{value, number, ::percent}"
              description="Formatted percentage value in cost share table, e.g. '80%'"
              values={{ value: 1 }}
            />
          </Typography>
          <Typography variant="body1" color={(theme) => theme.palette.text.secondary} textAlign="right">
            <FormattedMessage
              defaultMessage="{value, number, ::percent}"
              description="Formatted percentage value in cost share table, e.g. '80%'"
              values={{ value: 0 }}
            />
          </Typography>
        </Box>

        <Box
          sx={(theme) => ({
            paddingBlockStart: theme.spacing(theme.tokens.spacing.xs),
            paddingBlockEnd: theme.spacing(theme.tokens.spacing.xs),
            display: "grid",
            gridTemplateColumns: rowColumns,
            columnGap: theme.spacing(theme.tokens.spacing.lg),
            alignItems: "start",
            inlineSize: "100%"
          })}
        >
          <Stack display="flex" gap={(theme) => theme.tokens.spacing.xs}>
            <Typography
              component="span"
              variant="body1"
              fontWeight="bold"
              color={(theme) => theme.palette.text.secondary}
            >
              <FormattedMessage
                defaultMessage="Once you meet your deductible but before you meet your OOPM"
                description="Row label in cost share table. OOPM = out-of-pocket maximum, an insurance term"
              />
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontStyle: "italic", fontWeight: "normal" }}
              color={(theme) => theme.palette.text.secondary}
            >
              <FormattedMessage
                defaultMessage="*The amount {companyDisplayName} pays does not count toward your OOPM. We do report your own contributions to your insurance so they can count toward your OOPM."
                description="Footnote in cost share table. OOPM = out-of-pocket maximum, an insurance term"
                values={{ companyDisplayName: <PiiText>{companyDisplayName}</PiiText> }}
              />
            </Typography>
          </Stack>
          <Typography variant="body1" color={(theme) => theme.palette.text.secondary} textAlign="right">
            <FormattedMessage
              defaultMessage="{value, number, ::percent}"
              description="Formatted percentage value in cost share table, e.g. '80%'"
              values={{ value: oopNotMet.memberResponsibility / 100 }}
            />
          </Typography>
          <Typography variant="body1" color={(theme) => theme.palette.text.secondary} textAlign="right">
            <FormattedMessage
              defaultMessage="{value, number, ::percent}*"
              description="Formatted percentage with asterisk referencing a footnote in cost share table"
              values={{ value: oopNotMet.companyResponsibility / 100 }}
            />
          </Typography>
        </Box>

        <Box
          sx={(theme) => ({
            paddingBlockStart: theme.spacing(theme.tokens.spacing.xs),
            paddingBlockEnd: theme.spacing(theme.tokens.spacing.xs),
            display: "grid",
            gridTemplateColumns: rowColumns,
            columnGap: theme.spacing(theme.tokens.spacing.lg),
            alignItems: "start",
            inlineSize: "100%"
          })}
        >
          <Typography variant="body1" fontWeight="bold" color={(theme) => theme.palette.text.secondary}>
            <FormattedMessage
              defaultMessage="Once you meet your OOPM"
              description="Row label in cost share table. OOPM = out-of-pocket maximum, an insurance term"
            />
          </Typography>
          <Typography variant="body1" color={(theme) => theme.palette.text.secondary} textAlign="right">
            <FormattedMessage
              defaultMessage="{value, number, ::percent}"
              description="Formatted percentage value in cost share table, e.g. '80%'"
              values={{ value: oopMet.memberResponsibility / 100 }}
            />
          </Typography>
          <Typography variant="body1" color={(theme) => theme.palette.text.secondary} textAlign="right">
            <FormattedMessage
              defaultMessage="{value, number, ::percent}"
              description="Formatted percentage value in cost share table, e.g. '80%'"
              values={{ value: oopMet.companyResponsibility / 100 }}
            />
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}
